import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

const URLBasedAudioPlayer = ({ isOpen, onClose, audioInfo }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const iframeRef = useRef(null);

  // Fetch proper SoundCloud embed URL using oEmbed API
  const fetchEmbedUrl = async (url) => {
    if (!url) return "";

    try {
      const cleanUrl = url.trim();
      // Use SoundCloud's oEmbed API to get the proper embed URL
      const oembedUrl = `https://soundcloud.com/oembed?format=json&url=${encodeURIComponent(
        cleanUrl
      )}`;

      const response = await fetch(oembedUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch embed URL");
      }

      const data = await response.json();

      // Extract the src from the HTML iframe
      const srcMatch = data.html.match(/src="([^"]+)"/);
      if (srcMatch && srcMatch[1]) {
        // Decode HTML entities and add our custom parameters
        let embedSrc = srcMatch[1].replace(/&amp;/g, "&");

        // Add custom color parameter
        embedSrc = embedSrc.replace(/color=%23[^&]+/, "color=%23dc2626");

        // Add/update parameters for better control
        const params = new URLSearchParams(embedSrc.split("?")[1]);
        params.set("auto_play", "false");
        params.set("hide_related", "false");
        params.set("show_comments", "true");
        params.set("show_user", "true");
        params.set("show_reposts", "false");
        params.set("show_teaser", "true");

        return `${embedSrc.split("?")[0]}?${params.toString()}`;
      }

      return "";
    } catch (error) {
      console.error("Error fetching SoundCloud embed URL:", error);
      // Fallback to direct URL (may not work but worth trying)
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(
        url
      )}&color=%23dc2626&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setIframeLoaded(false);
      setEmbedUrl("");
    } else {
      // Reset iframe loaded state when opening
      setIframeLoaded(false);

      // Fetch the proper embed URL
      const loadEmbedUrl = async () => {
        if (audioInfo && audioInfo.link) {
          const url = await fetchEmbedUrl(audioInfo.link);
          setEmbedUrl(url);
        }
      };

      loadEmbedUrl();

      // Set a timeout to show content if iframe takes too long (fallback)
      const timer = setTimeout(() => {
        setIframeLoaded(true);
      }, 3000); // Show content after 3 seconds max

      return () => clearTimeout(timer);
    }
  }, [isOpen, audioInfo]);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen || !audioInfo) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-white rounded-lg shadow-2xl max-w-2xl w-full my-8 flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-800 to-red-600 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-xl font-bold">
                Liturgical Tone {audioInfo.tone}
              </h3>
              <p className="text-sm text-red-100 mt-1">{audioInfo.name}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {/* Loading Spinner */}
            {!iframeLoaded && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mb-4"></div>
                <p className="text-gray-600 text-lg font-medium">
                  Loading audio player...
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Please wait while we load the content
                </p>
              </div>
            )}

            {/* Content - Hidden until loaded */}
            <div className={iframeLoaded ? "block" : "hidden"}>
              {/* SoundCloud Embed */}
              <div className="mb-6">
                {embedUrl ? (
                  <iframe
                    ref={iframeRef}
                    width="100%"
                    height="166"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src={embedUrl}
                    title={`Tone ${audioInfo.tone} - ${audioInfo.name}`}
                    className="rounded-lg"
                    onLoad={handleIframeLoad}
                  />
                ) : (
                  <div className="text-center py-4 text-gray-600">
                    Loading player...
                  </div>
                )}
              </div>

              {/* Audio Element (Hidden - for future use if needed) */}
              {/* <audio
                ref={audioRef}
                src={audioInfo.audioUrl}
                preload="metadata"
              /> */}

              {/* Info */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="p-2">
                  <a
                    href={audioInfo.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-800 text-sm font-medium hover:underline"
                  >
                    Open in SoundCloud ↗
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default URLBasedAudioPlayer;
